<template>
    <div v-if="show" class="fixed inset-0 z-[200] flex items-center justify-center p-4">
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity" @click="$emit('close')"></div>

        <!-- Modal Content -->
        <div class="relative bg-[#202124] w-full max-w-sm rounded-[32px] shadow-2xl overflow-hidden animate-in zoom-in duration-300 border border-white/5">
            <!-- Header -->
            <div class="px-6 py-5 border-b border-white/5 flex items-center justify-between">
                <div class="flex items-center space-x-3">
                    <div class="p-2 bg-blue-500/10 rounded-xl">
                        <InformationCircleIcon class="h-5 w-5 text-blue-400" />
                    </div>
                    <h3 class="text-white text-base font-medium">{{ $t('chat.messageInfo') }}</h3>
                </div>
                <button @click="$emit('close')" class="text-gray-400 hover:text-white transition-colors">
                    <XIcon class="h-5 w-5" />
                </button>
            </div>

            <!-- Content -->
            <div class="max-h-[420px] overflow-y-auto custom-scrollbar bg-[#1a1b1e] p-2">
                <!-- Message Preview -->
                <div v-if="message" class="m-2 mb-4 p-4 bg-white/5 rounded-2xl border border-white/5">
                    <p class="text-sm text-gray-300 leading-relaxed italic line-clamp-3" translate="no">"{{ message.body || 'File attachment' }}"</p>
                    <div class="mt-2 flex items-center text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                        <span>{{ $t('chat.sent') }} {{ formatTime(message.sent_at) }}</span>
                    </div>
                </div>

                <div v-if="isLoading" class="flex flex-col items-center justify-center py-12 space-y-4">
                    <div class="h-8 w-8 border-3 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    <p class="text-sm text-gray-500 font-medium animate-pulse">{{ $t('chat.loading') }}</p>
                </div>

                <div v-else-if="seenBy.length > 0" class="space-y-1">
                    <div class="px-3 py-2 flex items-center justify-between">
                        <h4 class="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">{{ $t('chat.seenBy') }}</h4>
                        <span class="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 text-[10px] font-bold rounded-lg border border-emerald-500/20">
                            {{ seenBy.length }} {{ seenBy.length === 1 ? 'Person' : 'People' }}
                        </span>
                    </div>
                    
                    <div 
                        v-for="user in seenBy" 
                        :key="user.user_id" 
                        class="flex items-center justify-between p-3 rounded-2xl transition-all group hover:bg-white/5"
                    >
                        <div class="flex items-center space-x-4">
                            <div class="relative">
                                <img 
                                    :src="user.user_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.user_name)}&background=random`" 
                                    class="h-11 w-11 rounded-full object-cover border border-white/10 shadow-sm"
                                />
                                <div class="absolute -bottom-0.5 -right-0.5 h-3 w-3 bg-emerald-500 rounded-full border-2 border-[#1a1b1e]"></div>
                            </div>
                            <div class="flex flex-col">
                                <span class="text-sm font-medium text-white">{{ user.user_name }}</span>
                                <span class="text-[10px] text-gray-500">
                                    {{ user.seen_at ? $t('chat.read') + ' ' + formatRelativeTime(user.seen_at) : $t('chat.seen') }}
                                </span>
                            </div>
                        </div>
                        <div class="flex flex-col items-end">
                            <span v-if="user.seen_at" class="text-[10px] font-bold text-gray-500 uppercase tracking-tighter">{{ formatTime(user.seen_at) }}</span>
                            <div class="flex -space-x-1 mt-1">
                                <CheckIcon class="h-4 w-4 text-blue-500" />
                                <CheckIcon class="h-4 w-4 text-blue-500" />
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else class="flex flex-col items-center justify-center py-12 text-center">
                    <div class="h-16 w-16 bg-white/5 rounded-full flex items-center justify-center mb-4">
                        <CheckIcon class="h-8 w-8 text-gray-600" />
                    </div>
                    <h4 class="text-sm font-medium text-gray-300">{{ $t('chat.notSeenYet') }}</h4>
                    <p class="text-xs text-gray-500 mt-1 max-w-[200px]">{{ $t('chat.waitingParticipants') }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { InformationCircleIcon, XIcon, CheckIcon } from "@heroicons/vue/solid";
import moment from 'moment';

export default {
    name: 'MessageInfoModal',
    components: { InformationCircleIcon, XIcon, CheckIcon },
    props: {
        show: { type: Boolean, default: false },
        message: { type: Object, default: null },
        seenBy: { type: Array, default: () => [] },
        isLoading: { type: Boolean, default: false }
    },
    emits: ['close'],
    methods: {
        formatTime(date) {
            if (!date) return '';
            return moment(date).format('HH:mm');
        },
        formatRelativeTime(date) {
            if (!date) return '';
            return moment(date).fromNow();
        }
    }
};
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
    width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 10px;
}
</style>
